// pushToastr.js
'use strict';
import { PUSH_OPERATION_DATA, SET_OPERATION_DATA } from '../ActionTypes.js';
import getOperationData from '../selector/getOperationData.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { toastrColor as colorKeys } from '../style/variables.js';
import { v4 as uuidv4 } from 'uuid';
import {
  ToastrType,
  ToastrDuration,
} from '../resource/popupNotificationConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import removeModals from '../action/removeModals.js';
import processToastrs from '../action/processToastrs.js';

const toastrTemplate = {
  id: '',
  textKey: '',
  textKeyOption: null,
  durationSec: ToastrDuration.SHORT,
  color: 'rgba(0, 210, 190, 0.6)',
  count: 1,
  type: ToastrType.INFO,
  content: '',
  primaryButton: null,
  secondaryButton: null,
};

/**
 * Push toastr
 * @kind action
 * @param {string} {textKey} - text key for i18n.
 * @param {object|array} {textKeyOption} - option for i18n.
 * @param {string} {attachedMessage} - Message appended after i18n text.
 * @param {number} {durationSec} - seconds for toastr duration.
 * @param {string} {color} - 'success', 'error' or color codes.
 * @param {string} {type} - Toastr type ('info', 'complete', 'error').
 * @param {string} {content} - Additional content text.
 * @param {object} {primaryButton} - Primary button config ({ text, onClick }).
 * @param {object} {secondaryButton} - Secondary button config ({ text, onClick }).
 * @return {Promise} Action promise.
 */

const pushToastr =
  ({
    textKey,
    textKeyOption = {},
    attachedMessage = '',
    durationSec = ToastrDuration.DEFAULT,
    color = 'success',
    type: inputType = color,
    content = '',
    primaryButton = null,
    secondaryButton = null,
  }) =>
  async (dispatch, getState) => {
    const stacks = getOperationData(getState(), ['toastr'], 'stacks') || [];
    const existIndex = stacks.findIndex(tItem =>
      tItem.textKey.startsWith(textKey)
    );

    const validTypes = Object.values(ToastrType);
    const finalType = validTypes.includes(inputType)
      ? inputType
      : ToastrType.INFO;

    const hasPrimaryButton = primaryButton?.text && primaryButton?.onClick;
    const hasSecondaryButton =
      secondaryButton?.text && secondaryButton?.onClick;
    const hasButtons = hasPrimaryButton || hasSecondaryButton;

    const createButtonClickHandler = button => {
      if (!button || !button.onClick) return NULL_FUNCTION;
      const originalOnClick = button.onClick;

      return async event => {
        await originalOnClick(event);

        const currentStacks =
          getOperationData(getState(), ['toastr'], 'stacks') || [];
        const toastIdToRemove = toastr.id;

        dispatch({
          type: SET_OPERATION_DATA,
          payload: {
            selectPath: ['toastr'],
            data: {
              stacks: currentStacks.filter(t => t.id !== toastIdToRemove),
            },
          },
        });

        // Remove toastr modal and process remaining toastrs
        dispatch(removeModals({ ids: ['Toastr'] }));
        dispatch(processToastrs());
      };
    };

    let toastr;

    if (existIndex === -1) {
      toastr = {
        ...toastrTemplate,
        id: uuidv4(),
        color: colorKeys[color] || color,
        textKey,
        textKeyOption: Array.isArray(textKeyOption)
          ? {
              ...textKeyOption,
              ns: TranslationNamespace.NOTIFICATION,
            }
          : {
              ns: TranslationNamespace.NOTIFICATION,
              ...textKeyOption,
            },
        durationSec: hasButtons ? ToastrDuration.LONG : durationSec,
        attachedMessage,
        type: finalType,
        content,
        primaryButton: hasPrimaryButton
          ? {
              ...primaryButton,
              onClick: createButtonClickHandler(primaryButton),
            }
          : null,
        secondaryButton: hasSecondaryButton
          ? {
              ...secondaryButton,
              onClick: createButtonClickHandler(secondaryButton),
            }
          : null,
      };

      dispatch({
        type: PUSH_OPERATION_DATA,
        payload: {
          selectPath: ['toastr', 'stacks'],
          data: toastr,
        },
      });
    } else {
      const existTime = stacks[existIndex].count || 1;
      stacks[existIndex].count = existTime + 1;
      stacks[existIndex].type = finalType;
      const firstStartTimestamp = getOperationData(
        getState(),
        ['toastr'],
        'firstStartTimestamp'
      );
      dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['toastr'],
          data: {
            firstStartTimestamp: existIndex === 0 ? null : firstStartTimestamp,
            stacks,
          },
        },
      });
    }

    return dispatch(processToastrs());
  };

export default pushToastr;
